import type { SqliteExecutor } from './sqliteExecutor.ts'
import {
  configForEntity,
  parseRowData,
  projectOutput,
  queryEntityRows,
} from './catalogReplicaRepo.ts'

// Generic nested-read resolver for the offline replica. There is no GraphQL
// engine on the client — catalogClient is a document→handler map — so a query
// that selects across relations (releaseEntry → seriesInfo → staffs → individual)
// is served by walking a codegen-emitted plan: query the root entity's flat
// blobs, then for each related field read the `{ id }` link the blob carries
// (relations are stored normalized, not embedded, to avoid child-edit drift),
// fetch the child entity's blob, and recurse. Scalars + blob-embedded objects
// (e.g. `description { document }`) are projected straight from the blob.

type RowData = Record<string, unknown>

// A node in the selection plan: how to project + recurse one entity level.
// `via` distinguishes how the child rows are reached:
// - 'blobLink': the parent blob carries the child id(s) as `{ id }` / `[{ id }]`
//   (forward FK + implicit m2m, kept current by the entity/membership triggers)
// - 'reverseFk': the child table holds a foreign key back to the parent; resolved
//   live by querying the child table (always fresh, no parent re-stamp needed)
export type CatalogFieldPlan = {
  field: string
  entity: string
  isList: boolean
  via: 'blobLink' | 'reverseFk'
  foreignKey?: string
  outputFields: readonly string[]
  relations: readonly CatalogFieldPlan[]
  computed: readonly ComputedFieldPlan[]
}

// A field the server computes, not stored as a scalar. Declared in the generator's
// allowlist and resolved generically here (no per-query code).
export type ComputedFieldPlan = {
  field: string
  kind: 'relationCount'
  childEntity: string
  foreignKey: string
}

// A registered nested read: the root entity + how its root field is shaped
// (single object via a unique where, or a where/orderBy/take/skip list).
export type CatalogNestedOp = {
  entity: string
  field: string
  isList: boolean
  outputFields: readonly string[]
  relations: readonly CatalogFieldPlan[]
  computed: readonly ComputedFieldPlan[]
}

type OrderByArg = Record<string, 'asc' | 'desc'>

type NestedVariables = {
  where?: Record<string, unknown>
  // Single-or-array mirrors GraphQL list-input coercion; buildOrderBy normalizes it.
  orderBy?: OrderByArg | OrderByArg[]
  take?: number | null
  skip?: number
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function linkIds(link: unknown): string[] {
  if (!Array.isArray(link)) return []
  return link.map((item) => (item as { id?: unknown }).id).filter(isString)
}

async function fetchByIds(
  executor: SqliteExecutor,
  table: string,
  ids: readonly string[],
): Promise<Map<string, RowData>> {
  if (ids.length === 0) return new Map()
  const placeholders = ids.map(() => '?').join(', ')
  const rows = await executor.query(
    `SELECT data FROM "${table}" WHERE "id" IN (${placeholders})`,
    [...ids],
  )
  return new Map(
    rows.map((row) => {
      const data = parseRowData(row)
      return [data.id as string, data]
    }),
  )
}

// The parent link ids for one relation, per parent row, in the parent's order:
// a to-many carries `[{ id }]`, a to-one a single `{ id }` (normalized to a
// one- or zero-element list so both share the scatter path).
function blobLinkIdsPerRow(rows: readonly RowData[], relation: CatalogFieldPlan): string[][] {
  return rows.map((row) => {
    if (relation.isList) return linkIds(row[relation.field])
    const id = (row[relation.field] as { id?: unknown } | null | undefined)?.id
    return isString(id) ? [id] : []
  })
}

// reverseFk children for a whole level in ONE query, grouped by the parent id
// their foreign key points at (vs one `WHERE fk = ?` per parent).
async function fetchByForeignKeyIn(
  executor: SqliteExecutor,
  table: string,
  foreignKey: string,
  parentIds: readonly string[],
): Promise<Map<string, RowData[]>> {
  const grouped = new Map<string, RowData[]>()
  const ids = [...new Set(parentIds)]
  if (ids.length === 0) return grouped
  const placeholders = ids.map(() => '?').join(', ')
  const rows = await executor.query(
    `SELECT "${foreignKey}" AS __fk, data FROM "${table}" WHERE "${foreignKey}" IN (${placeholders})`,
    [...ids],
  )
  for (const row of rows) {
    const fk = (row as Record<string, unknown>).__fk
    if (!isString(fk)) continue
    const list = grouped.get(fk) ?? []
    list.push(parseRowData(row))
    grouped.set(fk, list)
  }
  return grouped
}

// relationCount for a whole level in ONE grouped query; parents with no children
// are simply absent from the map (resolved to 0 at scatter time).
async function countByForeignKeyIn(
  executor: SqliteExecutor,
  table: string,
  foreignKey: string,
  parentIds: readonly string[],
): Promise<Map<string, number>> {
  const counts = new Map<string, number>()
  const ids = [...new Set(parentIds)]
  if (ids.length === 0) return counts
  const placeholders = ids.map(() => '?').join(', ')
  const rows = await executor.query(
    `SELECT "${foreignKey}" AS __fk, COUNT(*) AS count FROM "${table}" WHERE "${foreignKey}" IN (${placeholders}) GROUP BY "${foreignKey}"`,
    [...ids],
  )
  for (const row of rows) {
    const r = row as { __fk?: unknown; count?: unknown }
    if (isString(r.__fk)) counts.set(r.__fk, Number(r.count))
  }
  return counts
}

// Resolve one relation across ALL sibling rows at once: gather the level's child
// rows with a single query, recurse into them as one further level, then scatter
// the projected children back onto each parent in the parent's own order.
async function applyRelation(
  executor: SqliteExecutor,
  rows: readonly RowData[],
  outputs: RowData[],
  relation: CatalogFieldPlan,
): Promise<void> {
  const table = configForEntity(relation.entity).table

  if (relation.via === 'reverseFk') {
    if (!relation.foreignKey) throw new Error(`reverseFk relation ${relation.field} missing foreignKey`)
    const parentIds = rows.map((row) => row.id as string)
    const childrenByParent = await fetchByForeignKeyIn(executor, table, relation.foreignKey, parentIds)
    const allChildren = [...childrenByParent.values()].flat()
    const projected = await projectLevel(executor, allChildren, relation)
    const projectedByChild = new Map<RowData, RowData>(allChildren.map((child, i) => [child, projected[i]]))
    rows.forEach((row, i) => {
      const kids = (childrenByParent.get(row.id as string) ?? [])
        .map((child) => projectedByChild.get(child))
        .filter((child): child is RowData => child !== undefined)
      outputs[i][relation.field] = relation.isList ? kids : (kids[0] ?? null)
    })
    return
  }

  const idsPerRow = blobLinkIdsPerRow(rows, relation)
  const distinctIds = [...new Set(idsPerRow.flat())]
  const byId = await fetchByIds(executor, table, distinctIds)
  const childRows = distinctIds
    .map((id) => byId.get(id))
    .filter((child): child is RowData => child !== undefined)
  const projected = await projectLevel(executor, childRows, relation)
  const projectedById = new Map<string, RowData>(childRows.map((child, i) => [child.id as string, projected[i]]))
  rows.forEach((row, i) => {
    const kids = idsPerRow[i]
      .map((id) => projectedById.get(id))
      .filter((child): child is RowData => child !== undefined)
    outputs[i][relation.field] = relation.isList ? kids : (kids[0] ?? null)
  })
}

async function applyComputed(
  executor: SqliteExecutor,
  rows: readonly RowData[],
  outputs: RowData[],
  computed: ComputedFieldPlan,
): Promise<void> {
  if (computed.kind !== 'relationCount') {
    rows.forEach((_, i) => {
      outputs[i][computed.field] = null
    })
    return
  }
  const table = configForEntity(computed.childEntity).table
  const counts = await countByForeignKeyIn(
    executor,
    table,
    computed.foreignKey,
    rows.map((row) => row.id as string),
  )
  rows.forEach((row, i) => {
    outputs[i][computed.field] = counts.get(row.id as string) ?? 0
  })
}

// Project a whole level of same-plan rows at once: scalars are mapped directly,
// then every relation + computed field is resolved batched across all rows (each
// recursing one level deeper), so total queries are O(depth × fields) regardless
// of row count instead of the per-row N+1 fan-out.
async function projectLevel(
  executor: SqliteExecutor,
  rows: readonly RowData[],
  plan: Pick<CatalogFieldPlan, 'outputFields' | 'relations' | 'computed'>,
): Promise<RowData[]> {
  const outputs = rows.map((data) => projectOutput(data, plan.outputFields))
  // Sequential, not Promise.all: SqliteExecutor wraps a single shared connection
  // and the Capacitor SQLite plugin doesn't queue concurrent calls, so overlapping
  // db.query() across branches can collide. The batching win is the reduced query
  // COUNT (one IN/GROUP BY per level per field), not parallelism.
  for (const relation of plan.relations) {
    await applyRelation(executor, rows, outputs, relation)
  }
  for (const computed of plan.computed) {
    await applyComputed(executor, rows, outputs, computed)
  }
  return outputs
}

function uniqueWhereId(where: unknown): string | null {
  const id = (where as { id?: unknown } | undefined)?.id
  return isString(id) ? id : null
}

// Resolve a registered nested op against the replica. List roots filter via the
// shared where/orderBy builder; single roots read one blob by its unique id.
export async function resolveNestedOp(
  executor: SqliteExecutor,
  op: CatalogNestedOp,
  variables: NestedVariables,
): Promise<{ data: Record<string, unknown> }> {
  const config = configForEntity(op.entity)

  if (op.isList) {
    const rows = await queryEntityRows(executor, config, {
      where: variables.where,
      orderBy: variables.orderBy,
      take: variables.take,
      skip: variables.skip,
    })
    return { data: { [op.field]: await projectLevel(executor, rows, op) } }
  }

  const id = uniqueWhereId(variables.where)
  if (id === null) return { data: { [op.field]: null } }
  const row = (await fetchByIds(executor, config.table, [id])).get(id)
  return { data: { [op.field]: row ? (await projectLevel(executor, [row], op))[0] : null } }
}
